import { useTranslation } from "react-i18next";
import { FormikProps } from "formik";
import Summary, { SummaryItem } from "../../common/Summary";
import { HugeiconsIcon } from "@hugeicons/react";
import AttachmentsComponent, {
  AttachmentRenderType,
} from "components/common/attachments/AttachmentComponent";
import { AttachmentDrawerType } from "enums/AttachmentDrawerType";
import { ImageAdd01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { ATTACHMENT_PARENT_TYPE } from "enums/AttachmentParentType";
import { LocalImageAsset } from "hooks/useAttachmentAndAssetHandling";
import { formatCashFull } from "utils/formatCash";
import { ContactRouterDrawerType } from "enums/ContactRouterDrawerType";
import { useEffect, useState } from "react";
import ContactDrawerRouter from "../contacts/ContactDrawerRouter";
import {
  paymentStatusLabelKey,
  workflowStatusLabelKey,
} from "utils/payments/paymentStatus";
import dayjs from "dayjs";
import {
  canCancelPayment,
  canPayNow,
  canMarkAsPaid,
  FormikPayment,
} from "utils/payments/paymentFormat";
import PaymentStatus from "enums/PaymentStatus";
import {
  Box,
  Button,
  Link as MuiLink,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CancelPaymentModal,
  MarkAsPaidModal,
  PayNowModal,
} from "./PaymentModules";
import { PaymentLoadingStates } from "hooks/usePaymentActions";
import UserProfileDrawer from "components/common/users/UserProfileDrawer";
import PaymentReceivedFromType from "enums/PaymentReceivedFromType";
import ApprovalDetails, {
  WorkflowRequest,
} from "components/common/ApprovalDetails";
import { Inline, Stack } from "components/layout/reusableLayouts";
import SectionTabs, { type SectionTab } from "components/common/SectionTabs";
import { RenderPaymentMethod } from "components/common/payments/RenderPaymentMethod";
import PaymentDrawerRouter from "./PaymentDrawerRouter";
import {
  RenderOption,
  RenderTransactionType,
} from "components/common/RenderTransactionType";
import TransactionDetailsDrawer from "components/common/transactions/TransactionDetailsDrawer";
import TransactionsType from "enums/TransactionsType";
import WorkflowRequestStatus from "enums/WorkflowRequestStatus";
import useCustomFieldRenderer from "hooks/useCustomFieldRenderer";
import CustomFieldParentType from "enums/CustomFieldParentType";
import { FormikCustomField } from "hooks/customFields.types";
import FormikCustomFields from "components/common/FormikCustomFields";
import Role from "enums/Role";
import PolicyIcons from "components/common/PolicyIcons";
import { RequirementSections } from "components/common/requirements/RequirementSections";
import ExpenseCategorySearchAutoComplete from "components/common/CategorySearchAutoComplete";
import ViolationStatus from "enums/ViolationStatus";
import AllocationSearchAutoComplete from "components/common/AllocationSearchAutoComplete";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import SectionGlyph from "components/common/SectionGlyph";

interface PaymentDetailsSummaryProps {
  formikPayment: FormikProps<FormikPayment>;
  customFieldSettings?: FormikCustomField[];
  specificLoadingStates?: PaymentLoadingStates;
  workflowRequest?: WorkflowRequest | null;
  setExpenseCategoryModified?: (value: boolean) => void;
  setAllocationModified?: (value: boolean) => void;
  /**
   * Fires while a nested drawer that carries its own `CommentsPanel` is open,
   * so a parent holding one can step it aside. Every panel is fixed at the same
   * z-index and offset, so two visible at once overlap. Pass a `useState`
   * setter: this reports through an effect and needs a stable identity.
   */
  onNestedCommentsChange?: (open: boolean) => void;
  handleLocalFileUpload: (newAssets: LocalImageAsset[]) => void;
  handleRemoveAttachments: (assetId: string) => void;
  refetchPayment?: () => void;
  onApprove?: () => void;
  onReject?: () => void;
}

const PaymentDetailsSummary = (props: PaymentDetailsSummaryProps) => {
  const {
    formikPayment,
    customFieldSettings,
    specificLoadingStates,
    workflowRequest,
    setExpenseCategoryModified,
    setAllocationModified,
    onNestedCommentsChange,
    handleLocalFileUpload,
    handleRemoveAttachments,
    refetchPayment,
    onApprove,
    onReject,
  } = props;
  const { t } = useTranslation();
  const [transactionReviewDrawerId, setTransactionReviewDrawerId] = useState<
    string | null
  >(null);
  const [openContactDetailsDrawer, setOpenContactDetailsDrawer] = useState<
    string | undefined
  >(undefined);
  const [reusePaymentValues, setReusePaymentValues] = useState<
    FormikPayment | undefined
  >(undefined);
  const [openReuseDrawer, setOpenReuseDrawer] = useState(false);
  const [openUserDrawer, setOpenUserDrawer] = useState<string | undefined>(
    undefined,
  );
  const [openPayNowModal, setOpenPayNowModal] = useState(false);
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [openMarkAsPaidModal, setOpenMarkAsPaidModal] = useState(false);

  const paymentData = formikPayment.values;
  const paymentId = paymentData.id || "";

  // OUT-1557: this was `isAdminOrCreator` via useCheckUserRoleWithSelf, which
  // compared the auth-provider id to a backend primary key and so never matched
  // the creator. Named for what it actually does until that is restored.
  const isAdminOrCreator = useCheckUserRole(Role.Admin);

  const isAuditor = useCheckUserRole(Role.Auditor, { exact: true });

  const { filteredCustomFieldSummaryItems, hasVisibleCustomFields } =
    useCustomFieldRenderer({
      parentType: CustomFieldParentType.PAYMENT,
      customFields: formikPayment.values.customFields,
    });

  // Only these two of the five nested drawers carry a CommentsPanel; the
  // contact, user and allocation drawers have none and cannot conflict.
  const nestedComments = openReuseDrawer || !!transactionReviewDrawerId;

  useEffect(() => {
    onNestedCommentsChange?.(nestedComments);
  }, [nestedComments, onNestedCommentsChange]);

  const approvalStatusKey = workflowStatusLabelKey(workflowRequest?.status);

  const summaryItems: SummaryItem[] = [
    {
      title: t("Sender"),
      value: paymentData.receivedFrom?.name || paymentData.receivedFrom?.email,
      onClick: () => {
        if (!paymentData.receivedFrom) return;
        if (paymentData.receivedFrom.type === PaymentReceivedFromType.USER) {
          setOpenUserDrawer(paymentData.receivedFrom.id);
        } else if (
          paymentData.receivedFrom.type === PaymentReceivedFromType.CONTACT
        ) {
          setOpenContactDetailsDrawer(paymentData.receivedFrom.id);
        } else if (
          paymentData.receivedFrom.type === PaymentReceivedFromType.EXTERNAL
        ) {
          if (paymentData.receivedFrom.email) {
            window.open(`mailto:${paymentData.receivedFrom.email}`, "_blank");
          }
        }
      },
    },
    {
      title: t("Description"),
      fullWidth: true,
      value: paymentData.description,
    },
    {
      title: t("Due Date"),
      value:
        paymentData.dueDate &&
        dayjs(paymentData.dueDate).format("MMMM D, YYYY"),
    },
    {
      title: t("Payment Recipient"),
      value: paymentData.payTo?.name,
      onClick: () => setOpenContactDetailsDrawer(paymentData?.payTo?.id),
    },
    {
      title: t("Payment Date"),
      value:
        paymentData.scheduledPaymentDate &&
        dayjs(paymentData.scheduledPaymentDate).format("MMMM D, YYYY"),
    },
    {
      title: t("Paid Date"),
      value:
        paymentData.actualPaymentDate &&
        dayjs(paymentData.actualPaymentDate).format("MMMM D, YYYY"),
    },
    {
      title: t("Payment Amount"),
      value:
        paymentData?.amount !== 0 &&
        formatCashFull(paymentData?.amount, paymentData?.currency),
    },
    {
      title: t("Pay From"),
      fullWidth: true,
      value:
        paymentData.payFrom?.name &&
        `${paymentData.payFrom.name || ""} (ending in ${paymentData.payFrom.last4 || "----"})`,
    },
    {
      title: t("Payment Method"),
      value: paymentData.paymentMethod && (
        <RenderPaymentMethod method={paymentData.paymentMethod} />
      ),
    },
    {
      title: t("Status"),
      value: paymentData.status && t(paymentStatusLabelKey(paymentData.status)),
    },
    {
      title: t("Approval Status"),
      value:
        paymentData.status !== PaymentStatus.DRAFT && approvalStatusKey
          ? t(approvalStatusKey)
          : null,
    },
    {
      title: t("Linked Transaction"),
      value: paymentData?.linkedTransaction?.id && (
        <RenderTransactionType
          transactionType={
            paymentData?.linkedTransaction.type as TransactionsType
          }
          cardType={""}
          renderOption={RenderOption.TEXT}
        />
      ),
      onClick: () =>
        setTransactionReviewDrawerId(paymentData?.linkedTransaction?.id || ""),
    },
    {
      title: t("Notes"),
      fullWidth: true,
      value: paymentData.notes,
    },
    ...(filteredCustomFieldSummaryItems || []),
  ];

  const isSummaryReady = !specificLoadingStates?.loadingPayment;

  const loadingApproveReject =
    specificLoadingStates?.approveLoading ||
    specificLoadingStates?.rejectLoading ||
    specificLoadingStates?.loadingPayment;

  const canCancelOrPayNow =
    paymentId &&
    (paymentData.status === PaymentStatus.PENDING_PAYMENT ||
      canCancelPayment(paymentData.status as PaymentStatus));

  const showCancelPayment = canCancelPayment(
    paymentData.status as PaymentStatus,
  );

  const showMarkPaid = canMarkAsPaid(paymentData.status as PaymentStatus);

  const showPayNow =
    canPayNow(paymentData.status as PaymentStatus) &&
    (!workflowRequest ||
      workflowRequest.status === WorkflowRequestStatus.APPROVED);

  const showCustomFields =
    isAdminOrCreator && !!paymentId && hasVisibleCustomFields;

  /**
   * Attachments, category and allocation as one panel, matching the transaction
   * review drawer: they are one question (is this payment ready to send), so
   * they share a tab and their glyphs stay on the section headings where all
   * three read at a glance.
   */
  const categoryStatus = formikPayment.values.expenseCategory
    ? ViolationStatus.MATCH
    : ViolationStatus.MISSING;
  const allocationStatus = formikPayment.values.allocationCode
    ? ViolationStatus.MATCH
    : ViolationStatus.MISSING;

  const requirementsPanel = (
    <RequirementSections
      recordId={paymentId}
      sections={[
        {
          key: "attachments",
          title: t("Attachments"),
          icon: (
            <SectionGlyph aria-label={t("Attachments")}>
              <HugeiconsIcon icon={ImageAdd01Icon} size={16} />
            </SectionGlyph>
          ),
          needsAction: false,
          content: (
            <AttachmentsComponent
              renderType={AttachmentRenderType.ACCORDION}
              drawerType={AttachmentDrawerType.ATTACHMENT}
              parentDetails={{
                parentId: paymentId || "", // NOTE: THIS CAN UPDATE THE BACKEND
                parentType: ATTACHMENT_PARENT_TYPE.PAYMENT,
              }}
              localAssets={formikPayment.values.attachments}
              onAddAssets={handleLocalFileUpload}
              onRemoveAssets={handleRemoveAttachments}
            />
          ),
        },
        {
          key: "categories",
          title: t("Expense Categories"),
          icon: (
            <PolicyIcons
              violationStatus={categoryStatus}
              violationType="category"
            />
          ),
          needsAction: categoryStatus === ViolationStatus.MISSING,
          content: (
            <ExpenseCategorySearchAutoComplete
              expenseCategory={formikPayment.values.expenseCategory || null}
              onSelectExpenseCategory={(expenseCategory) => {
                formikPayment.setFieldValue("expenseCategory", expenseCategory);
                setExpenseCategoryModified?.(true);
              }}
              hideClearButton={true}
            />
          ),
        },
        {
          key: "allocations",
          title: t("Allocations"),
          icon: (
            <PolicyIcons
              violationStatus={allocationStatus}
              violationType="allocation"
            />
          ),
          needsAction: allocationStatus === ViolationStatus.MISSING,
          content: (
            <Stack gap="var(--space-6)">
              <AllocationSearchAutoComplete
                title={null}
                allocation={formikPayment.values.allocationCode || null}
                allocationType={formikPayment.values.allocationType}
                onSelectAllocation={(allocation) => {
                  if (!allocation) {
                    formikPayment.setFieldValue("allocationCode", null);
                    formikPayment.setFieldValue("allocationType", null);
                  } else {
                    formikPayment.setFieldValue("allocationCode", {
                      id: allocation.id,
                      allocationCode: allocation.allocationCode,
                    });
                    formikPayment.setFieldValue(
                      "allocationType",
                      allocation.allocationType,
                    );
                  }
                  setAllocationModified?.(true);
                }}
              />
            </Stack>
          ),
        },
      ]}
    />
  );

  const sectionTabs: SectionTab[] = [
    {
      key: "requirements",
      label: t("Requirements"),
      panel: requirementsPanel,
    },
    ...(showCustomFields
      ? [
          {
            key: "customFields",
            label: t("Custom Fields"),
            panel: (
              <FormikCustomFields
                settings={customFieldSettings}
                formik={formikPayment}
                variant="accordion"
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <Stack gap="var(--space-8)">
      <Summary
        disableContainer
        items={summaryItems}
        alignEndColumn
        loading={!isSummaryReady}
        showLoadingIcon={true}
        children={
          !isAuditor &&
          canCancelOrPayNow && (
            <Inline gap="var(--space-4)" justify="center">
              {showPayNow && (
                <Button
                  size="small"
                  variant="primary-filled"
                  onClick={() => setOpenPayNowModal(true)}
                >
                  {t("Pay Now")}
                </Button>
              )}
              {showMarkPaid && (
                <Tooltip title={t("MarkPaidOutsideSystem")}>
                  <Button
                    size="small"
                    variant="primary-soft"
                    onClick={() => setOpenMarkAsPaidModal(true)}
                  >
                    {t("Mark as Paid")}
                  </Button>
                </Tooltip>
              )}

              {showCancelPayment && (
                <Button
                  size="small"
                  variant="danger-outlined"
                  onClick={() => setOpenCancelModal(true)}
                >
                  {t("Cancel")}
                </Button>
              )}
            </Inline>
          )
        }
      />

      {!isAuditor && paymentId && (
        <Inline justify="end">
          <MuiLink
            component="button"
            type="button"
            variant="body2"
            onClick={() => {
              const values = formikPayment.values;
              setReusePaymentValues({
                // Brand-new payment: reset identity, lifecycle, and date fields so
                // nothing tying it to the original payment (or its processing)
                // carries over. The user re-picks the schedule in the drawer.
                id: "",
                status: undefined,
                actualPaymentDate: undefined,
                scheduledPaymentDate: undefined,
                dueDate: undefined,
                attachments: [],
                linkedTransaction: null,
                createdBy: undefined,
                receivedFrom: undefined,
                // Explicitly copy only the fields that define what is being paid.
                // Listed out (not ...rest) so new FormikPayment fields are never
                // carried over by accident.
                payTo: values.payTo,
                amount: values.amount,
                currency: values.currency,
                paymentMethod: values.paymentMethod,
                payFrom: values.payFrom,
                description: values.description,
                notes: values.notes,
                customFields: values.customFields,
                expenseCategory: values.expenseCategory,
                allocationCode: values.allocationCode,
                allocationType: values.allocationType,
              });

              setOpenReuseDrawer(true);
            }}
          >
            {t("Reuse this payment")}
          </MuiLink>
        </Inline>
      )}

      {!isAuditor &&
        workflowRequest &&
        paymentData.status &&
        paymentData.status !== PaymentStatus.DRAFT && (
          <div>
            <ApprovalDetails
              workflowRequest={workflowRequest}
              loadingApproveReject={loadingApproveReject}
              onApprove={onApprove}
              onReject={onReject}
            />
          </div>
        )}

      <Box>
        <SectionTabs tabs={sectionTabs} ariaLabel={t("Payment sections")} />
      </Box>

      <ContactDrawerRouter
        open={!!openContactDetailsDrawer}
        drawerType={ContactRouterDrawerType.ExistingContact}
        contactId={openContactDetailsDrawer}
        onClose={() => setOpenContactDetailsDrawer(undefined)}
      />

      {transactionReviewDrawerId && (
        <TransactionDetailsDrawer
          open={!!transactionReviewDrawerId}
          handleCloseDrawer={() => setTransactionReviewDrawerId(null)}
          transactionId={transactionReviewDrawerId}
        />
      )}

      <UserProfileDrawer
        selectedUserId={openUserDrawer || ""}
        open={!!openUserDrawer}
        handleCloseDrawer={() => setOpenUserDrawer(undefined)}
      />

      {openReuseDrawer && reusePaymentValues && (
        <PaymentDrawerRouter
          open={openReuseDrawer}
          reusePaymentValues={reusePaymentValues}
          handleClosePaymentDrawerRouter={(refresh) => {
            setOpenReuseDrawer(false);
            if (refresh && refetchPayment) {
              refetchPayment();
            }
            setReusePaymentValues(undefined);
          }}
        />
      )}

      {openCancelModal && paymentId && (
        <CancelPaymentModal
          open={openCancelModal}
          setOpen={setOpenCancelModal}
          paymentId={paymentId}
          handleUpdatePayments={refetchPayment}
        />
      )}

      {openMarkAsPaidModal && paymentId && (
        <MarkAsPaidModal
          open={openMarkAsPaidModal}
          setOpen={setOpenMarkAsPaidModal}
          paymentId={paymentId}
          handleUpdatePayments={refetchPayment}
        />
      )}

      {openPayNowModal && paymentId && (
        <PayNowModal
          open={openPayNowModal}
          setOpen={setOpenPayNowModal}
          paymentId={paymentId}
          handleUpdatePayments={refetchPayment}
        />
      )}
    </Stack>
  );
};

export default PaymentDetailsSummary;
